class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;

    const existingTimer = this.querySelector('lumen-cart-timer');
    const keepTimer = existingTimer && parsedState.item_count > 0;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      if (keepTimer && section.id === 'cart-drawer') {
        // Detach live timer before wiping #CartDrawer, then put it back
        existingTimer.remove();
        sectionElement.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        );
        const placeholder = sectionElement.querySelector('lumen-cart-timer');
        if (placeholder) placeholder.replaceWith(existingTimer);
        else {
          const header = sectionElement.querySelector('.lumen-cart__header, .drawer__header');
          if (header) header.insertAdjacentElement('afterend', existingTimer);
        }
        if (typeof existingTimer.syncFromCart === 'function') {
          existingTimer.syncFromCart(parsedState.item_count);
        } else {
          existingTimer.dataset.count = String(parsedState.item_count);
        }
        return;
      }

      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          // Keep the reservation timer mounted when the cart still has items
          if (parsedState.item_count > 0) {
            this.#patchDrawerSections(parsedState.sections['cart-drawer']);
            const bubble = this.getSectionsToRender().find((s) => s.id === 'cart-icon-bubble');
            if (bubble) {
              const el = document.getElementById(bubble.id);
              if (el) {
                el.innerHTML = this.getSectionInnerHTML(
                  parsedState.sections[bubble.section],
                  bubble.selector
                );
              }
            }
            const timer = document.querySelector('lumen-cart-timer');
            if (timer && typeof timer.syncFromCart === 'function') {
              timer.syncFromCart(parsedState.item_count);
            } else if (timer) {
              timer.dataset.count = String(parsedState.item_count);
            }
          } else {
            this.getSectionsToRender().forEach((section) => {
              const elementToReplace =
                document.getElementById(section.id).querySelector(section.selector) ||
                document.getElementById(section.id);
              if (!elementToReplace) return;
              elementToReplace.innerHTML = this.getSectionInnerHTML(
                parsedState.sections[section.section],
                section.selector
              );
            });
          }

          const updatedValue = parsedState.items[line - 1]
            ? parsedState.items[line - 1].quantity
            : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) ||
            document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(
              cartDrawerWrapper.querySelector('.drawer__inner-empty'),
              cartDrawerWrapper.querySelector('a')
            );
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: parsedState,
          variantId: variantId,
        });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  #patchDrawerSections(sectionHtml) {
    if (!sectionHtml) return;
    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');

    // Keep the same <cart-drawer-items> node so loading state / listeners survive
    const itemsTarget = document.querySelector('cart-drawer-items');
    const itemsSource = doc.querySelector('cart-drawer-items');
    if (itemsTarget && itemsSource) {
      itemsTarget.innerHTML = itemsSource.innerHTML;
      itemsTarget.className = itemsSource.className;
    }

    [
      ['.cart-drawer__footer', '.cart-drawer__footer'],
      ['ines-upsell', 'ines-upsell'],
      ['ines-cart-upsell', 'ines-cart-upsell'],
      ['cart-drawer-upsell', 'cart-drawer-upsell'],
      ['.lumen-cart__heading', '.lumen-cart__heading'],
      ['.ines-cart__ship', '.ines-cart__ship'],
      ['.ines-cart__tiers', '.ines-cart__tiers'],
    ].forEach(([targetSel, sourceSel]) => {
      const target = document.querySelector(targetSel);
      const source = doc.querySelector(sourceSel);
      if (!target && source) {
        // Upsell may appear after first add — insert before footer when missing
        if (
          targetSel === 'ines-upsell' ||
          targetSel === 'ines-cart-upsell' ||
          targetSel === 'cart-drawer-upsell'
        ) {
          const scroll = document.querySelector('.cart2__scroll');
          const footer = document.querySelector('.cart-drawer__footer');
          if (scroll) scroll.appendChild(source.cloneNode(true));
          else if (footer) footer.insertAdjacentElement('beforebegin', source.cloneNode(true));
        }
        return;
      }
      if (!target || !source) {
        if (
          target &&
          !source &&
          (targetSel === 'ines-upsell' ||
            targetSel === 'ines-cart-upsell' ||
            targetSel === 'cart-drawer-upsell')
        ) {
          target.remove();
        }
        return;
      }
      target.replaceWith(source.cloneNode(true));
    });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
